import { graphqlRequest, isGraphqlConfigured } from './graphqlClient';
import type { IntegrationProvider, TodoList } from '../types';

/** Every TodoList field the client reads. */
export const TODO_LIST_FIELDS = 'id userId name source provider integrationAccountId createdAt updatedAt';

export { isGraphqlConfigured as isTodoListsApiConfigured };

export const getTodoLists = async (): Promise<TodoList[]> => {
  const data = await graphqlRequest<{ todoLists: TodoList[] }>(`
    query TodoLists {
      todoLists { ${TODO_LIST_FIELDS} }
    }
  `);
  return data.todoLists || [];
};

/**
 * Where a new list lives (contract C22 / §7e). Omitted = a plain Rhemn list.
 * `accountId` picks the account; without it the server uses the user's only
 * account for that provider.
 */
export interface ListDestination {
  provider: IntegrationProvider;
  accountId?: string;
}

/** A provider create runs a synchronous provider call inside an 8 s server budget. */
const PROVIDER_CREATE_TIMEOUT_MS = 15_000;

export const createTodoList = async (name: string, destination?: ListDestination): Promise<TodoList> => {
  // The destination variable is declared ONLY when used: a query that names
  // ListDestinationInput fails validation on a server that predates C22, and
  // that must never break plain list creation.
  if (!destination) {
    const data = await graphqlRequest<{ createTodoList: TodoList }>(`
      mutation CreateTodoList($name: String!) {
        createTodoList(name: $name) { ${TODO_LIST_FIELDS} }
      }
    `, { name });
    return data.createTodoList;
  }
  const data = await graphqlRequest<{ createTodoList: TodoList }>(`
    mutation CreateTodoListAt($name: String!, $destination: ListDestinationInput) {
      createTodoList(name: $name, destination: $destination) { ${TODO_LIST_FIELDS} }
    }
  `, {
    name,
    destination: { provider: destination.provider, accountId: destination.accountId ?? null },
  }, { timeoutMs: PROVIDER_CREATE_TIMEOUT_MS });
  return data.createTodoList;
};

export const renameTodoList = async (id: string, name: string): Promise<TodoList> => {
  const data = await graphqlRequest<{ renameTodoList: TodoList }>(`
    mutation RenameTodoList($id: ID!, $name: String!) {
      renameTodoList(id: $id, name: $name) { ${TODO_LIST_FIELDS} }
    }
  `, { id, name });
  return data.renameTodoList;
};

export const deleteTodoList = async (id: string): Promise<boolean> => {
  const data = await graphqlRequest<{ deleteTodoList: boolean }>(`
    mutation DeleteTodoList($id: ID!) { deleteTodoList(id: $id) }
  `, { id });
  return !!data.deleteTodoList;
};

const todoListsApi = { isTodoListsApiConfigured: isGraphqlConfigured, getTodoLists, createTodoList, renameTodoList, deleteTodoList };
export default todoListsApi;
